import {
  DatastoreObjectType,
  SkMappedItems,
  PKMappedItems,
  NodeEdgemapperLike,
  HydrateableSkMappedItems,
  AdjacentType,
  ComparisonType,
  LogicalOperatorType,
  ValidBusinessObject,
} from '@iotv/iotv-v3-types'
import { DEBUG_OVERRIDE, NOT_LAMBDA } from '../../../util/lambda'
import { EdgeTypeMatrixClassNames, ValidModelObject, edgeTypeMatrix } from '../../../classDependentTypes'
import { NodeEdgeMapper } from './NodeEdgeMapper'
import { getZombieEdge } from '../../../util/TypeHelpers'

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)
const applicationServerObjectId: EdgeTypeMatrixClassNames = 'LoRaWANApplicationServer'

type SkMapInverter = <T extends DatastoreObjectType = DatastoreObjectType>(skMapped: SkMappedItems<T>) => PKMappedItems<T>

export const invertSkMapped: SkMapInverter = <T extends DatastoreObjectType = DatastoreObjectType>(skMapped: SkMappedItems<T>) => {
  const rePkMapped: PKMappedItems<T> = {}

  Object.entries(skMapped).forEach(([sk, pkMapped]) => {
    Object.entries(pkMapped).forEach(([pk, object]) => {
      if (!rePkMapped[pk]) {
        rePkMapped[pk] = {}
      }
      rePkMapped[pk][sk] = object
    })
  })

  debug && console.log(`pkMapped in invert has ${Object.keys(rePkMapped).length} keys`)
  return rePkMapped
}

export const dehydrateNodeEdgeMapperToHydrateableSKMapped = <T extends DatastoreObjectType = DatastoreObjectType>(
  nem: NodeEdgemapperLike<T>,
): HydrateableSkMappedItems<T> => {
  const dehydrahed = { ...nem } as HydrateableSkMappedItems<T> & { pkMapped?: PKMappedItems<T> }
  delete dehydrahed.pkMapped
  return dehydrahed
}

export const isNodeEdgeMapperLike = (x: any): x is NodeEdgemapperLike<any> => {
  const y = x as NodeEdgemapperLike<any>
  let isValid = false
  if (typeof y.pkMapped === 'object' && typeof y.skMapped === 'object') {
    isValid = true
  }
  return isValid
}

export type GetDeviceContextInput = {
  nodeEdgeMapper: NodeEdgeMapper<ValidBusinessObject>
  device: ValidModelObject<'Device'>
  /** the objecTypeIds of the possible entities attached to device - this can be acquired from getDeviceEnabledTypesIds in @iotv/payload-definitions where modules can import from this */
  deviceEnabledTypeIds: string[]
}

export type GetDeviceContextOutput = {
  device: ValidModelObject<'Device'>
  entity?: ValidBusinessObject
  group?: ValidBusinessObject
  customer?: ValidBusinessObject
  applicationServer?: ValidBusinessObject
  customerGroupEdge?: ValidBusinessObject
  groupDeviceEdge?: ValidBusinessObject
  property?: ValidBusinessObject
  place?: ValidBusinessObject
  deviceEntityEdge?: ValidBusinessObject
  entityPropertyEdge?: ValidBusinessObject
  entityPlaceEdge?: ValidBusinessObject
  applicationServerDeviceEdge?: ValidBusinessObject
}

/** resolves group and customer where group is directly related to customer, used to disambiguate multple groups */
export const getDeviceContextFromNodeEdgeMapper = ({ nodeEdgeMapper, device, deviceEnabledTypeIds }: GetDeviceContextInput) => {
  let deviceContext: GetDeviceContextOutput | undefined = undefined

  for (let deviceEnabledTypeId of deviceEnabledTypeIds) {
    const { nodes, edges } = nodeEdgeMapper.getAdjacentNodesAndEdges(device, { objectTypeId: deviceEnabledTypeId, adjacencyType: AdjacentType.PARENT })
    if (nodes.length > 0) {
      const entity = nodes[0] as ValidBusinessObject
      const deviceEntityEdge = (nodeEdgeMapper.getEdgeBetween(entity, device) ?? getZombieEdge(entity, device)) as ValidBusinessObject
      deviceContext = {
        device,
        entity,
        deviceEntityEdge,
      }

      const { nodes: propertyNodes, edges: propertyEdges } = nodeEdgeMapper.getAdjacentNodesAndEdges(entity, {
        objectTypeId: 'Property',
        adjacencyType: AdjacentType.PARENT,
      })
      if (propertyNodes.length > 0) {
        deviceContext.property = propertyNodes[0] as ValidBusinessObject
        deviceContext.entityPropertyEdge = (nodeEdgeMapper.getEdgeBetween(entity, deviceContext.property) ??
          getZombieEdge(entity, deviceContext.property)) as ValidBusinessObject
      }

      const { nodes: placeNodes, edges: placeEdges } = nodeEdgeMapper.getAdjacentNodesAndEdges(entity, {
        objectTypeId: 'Place',
        adjacencyType: AdjacentType.CHILD,
      })
      if (placeNodes.length > 0) {
        deviceContext.place = placeNodes[0] as ValidBusinessObject
        deviceContext.entityPlaceEdge = (nodeEdgeMapper.getEdgeBetween(entity, deviceContext.place) ??
          getZombieEdge(entity, deviceContext.place)) as ValidBusinessObject
      }

      const { nodes: applicationServerNodes } = nodeEdgeMapper.getAdjacentNodesAndEdges(device, {
        objectTypeId: applicationServerObjectId,
        adjacencyType: AdjacentType.PARENT,
      })
      if (applicationServerNodes.length > 0) {
        deviceContext.applicationServer = applicationServerNodes[0] as ValidBusinessObject
        deviceContext.applicationServerDeviceEdge = (nodeEdgeMapper.getEdgeBetween(deviceContext.applicationServer, device) ??
          getZombieEdge(
            deviceContext.applicationServer,
            device,
            edgeTypeMatrix.LoRaWANApplicationServer.Device.networkApplicatonServer_has_device.edgeTypeId,
          )) as ValidBusinessObject
      }

      break
    }
  }

  const groups = nodeEdgeMapper.getAdjacentNodesAndEdges(device, {
    objectTypeId: 'Group',
    adjacencyType: AdjacentType.PARENT,
    edgeTypeId: edgeTypeMatrix.Group.Device.group_has_device.edgeTypeId,
    nodeFilters: [
      {
        setOperation: LogicalOperatorType.AND,
        filters: [{ key: 'supportedType', predicate: ComparisonType.EQUALS, value: 'Device' }],
      },
    ],
  })

  /** dev */

  debug && console.log('getDeviceContextFromNodeEdgeMapper nem types ', nodeEdgeMapper.getSummary())

  if (groups.nodes.length > 0) {
    for (let group of groups.nodes) {
      const customers = nodeEdgeMapper.getAdjacentNodesAndEdges(group, {
        objectTypeId: 'Customer',
        adjacencyType: AdjacentType.PARENT,
        edgeTypeId: edgeTypeMatrix.Customer.Group.customer_has_group.edgeTypeId,
      })
      if (customers.nodes.length > 0) {
        const customer = customers.nodes[0] as ValidBusinessObject
        const customerGroupEdge = nodeEdgeMapper.getItem(getZombieEdge(customer as ValidBusinessObject, group as ValidBusinessObject)) as ValidBusinessObject
        const groupDeviceEdge = nodeEdgeMapper.getItem(getZombieEdge(group as ValidBusinessObject, device)) as ValidBusinessObject
        if (group && customer && customerGroupEdge && groupDeviceEdge) {
          const assignable = {
            device,
            group: group as ValidBusinessObject,
            customer,
            customerGroupEdge,
            groupDeviceEdge,
          }

          if (!deviceContext) {
            deviceContext = assignable
          } else {
            Object.assign(deviceContext, assignable)
          }
        }
        break
      }
    }
  }
  return deviceContext
}
